import exportFromJSON from "export-from-json";
import { useTranslation } from "react-i18next";
import userSettingsService from "src/services/userSettingsService";
import store from "src/store";
import { useSelector } from "src/store";
import { useLicenses } from "src/ui/containers/dashboard/licenses/api/helpers";
import { useAnalyticsClick } from "src/ui/hooks/useAnalyticsClick";

import { DropdownItem } from "@dashboard/devices/components/shared/Dropdown/DropdownItem";
import { filterLicensesByProperties } from "@dashboard/devices/store/helpers/index";
import { selectDevicesByLicenseIds } from "@dashboard/devices/store/selectors/devices";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Device, License } from "@devices/types";

type FilterStateProperties = {
    licenseStatus: string[];
    onlineStatus: string[];
    policyStatus: string[];
    systemVersion: string[];
    codename: string[];
    groupName: string[];
};

type LicenseData = Omit<License, "device"> & {
    "device": number | null;
};
type DeviceData = {
    "device id": number;
    "IP address": string;
    "SSID": string;
    "name": string;
    "description": string;
    "product": string;
    "serial": string;
};

function getFilterProperties(): FilterStateProperties {
    return userSettingsService.getDevicePageColumnFilters().reduce(
        (acc: FilterStateProperties, filter) => ({ ...acc, [filter.id]: filter.value as string[] }),
        { licenseStatus: [], onlineStatus: [], policyStatus: [], systemVersion: [], codename: [], groupName: [] },
    );
}

function buildLicenseCsvRows(licenses: License[]): LicenseData[] {
    return licenses
        .map((license) => ({ ...license, device: license?.device?.id ?? null }))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ id, groupId, ...rest }) => rest)
        .map((license) =>
            Object.entries(license).reduce(
                (acc, [key, value]) => ({ ...acc, [key]: value === null ? "" : value }),
                {} as LicenseData,
            ),
        );
}

function buildDeviceCsvRows(devices: Device[]): DeviceData[] {
    return devices
        .map((device) => ({
            "device id": device?.id,
            "IP address": device?.lastState?.ethernet?.ip ?? "",
            "SSID": device?.lastState?.wifi?.connected ? (device?.lastState?.wifi?.ssid ?? "") : "",
            "name": device?.name,
            "description": device?.description,
            "product": device?.product,
            "serial": device?.serial,
        }))
        .map((device) =>
            Object.entries(device).reduce(
                (acc, [key, value]) => ({ ...acc, [key]: value === null ? "" : value }),
                {} as DeviceData,
            ),
        );
}

function exportCsv(licensesData: LicenseData[], devicesData: DeviceData[]): void {
    if (!licensesData.length) {
        return;
    }

    if (!devicesData.length || !devicesData[0]) {
        exportFromJSON({
            data: licensesData,
            fields: Object.keys(licensesData[0]),
            fileName: "licenses",
            exportType: exportFromJSON.types.csv,
        });
        return;
    }

    const devicesById = new Map(devicesData.map((d) => [d["device id"], d]));
    const combinedData = licensesData.map((license) => ({
        ...license,
        ...devicesById.get(license.device as number),
    }));

    exportFromJSON({
        data: combinedData,
        fields: [...Object.keys(licensesData[0]), ...Object.keys(devicesData[0])],
        fileName: "licenses_with_devices",
        exportType: exportFromJSON.types.csv,
    });
}

const ExportCSV = () => {
    const trackClick = useAnalyticsClick();
    const { t } = useTranslation(["pages"]);
    const allLicenses = useLicenses();
    const groups = useSelector(selectGroups);
    const isExpired = useCurrentSubscriptionExpired();

    const handleClick = () => {
        const filters = getFilterProperties();
        const filteredLicenses = filterLicensesByProperties({ licenses: allLicenses, filters, groups });
        if (!filteredLicenses.length) {
            return;
        }

        const licenseIds = filteredLicenses.map((l) => l.id);
        const devices = selectDevicesByLicenseIds(store.getState(), licenseIds);

        exportCsv(buildLicenseCsvRows(filteredLicenses), buildDeviceCsvRows(devices));
    };

    return (
        <DropdownItem
            label={t("pages:devicePage.button.exportLicenses")}
            iconName="fa-solid fa-download"
            onClick={trackClick(handleClick, "export-licenses", "dropdown")}
            // All CSV data is computed lazily on click, so we only check allLicenses here rather than
            // filtered licenses. If the active filters happen to produce zero results, clicking Export
            // silently does nothing — acceptable given the significant render-time savings.
            disabled={!allLicenses.length || isExpired}
            tooltipProps={{ title: t("pages:devicePage.tooltip.subscriptionExpired"), hide: !isExpired }}
        />
    );
};

export default ExportCSV;
